// components/SystemHubSite.js
// Painel de gestão do Site Institucional (somente admin_geral).
// Abas: Vagas · Currículos · Conteúdo · Bot · Leads
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaBriefcase, FaFileAlt, FaNewspaper, FaRobot, FaBullhorn,
  FaPlus, FaTrash, FaEdit, FaExternalLinkAlt, FaSave, FaTimes,
} from 'react-icons/fa';
import './SystemHubSite.css';

const TABS = [
  { key: 'vagas',      label: 'Vagas',      icon: <FaBriefcase /> },
  { key: 'curriculos', label: 'Currículos', icon: <FaFileAlt /> },
  { key: 'conteudo',   label: 'Conteúdo',   icon: <FaNewspaper /> },
  { key: 'bot',        label: 'Bot',        icon: <FaRobot /> },
  { key: 'leads',      label: 'Leads',      icon: <FaBullhorn /> },
];

const SITE_URL = 'https://sistemablb.com.br';

export default function SystemHubSite() {
  const [tab, setTab] = useState('vagas');

  return (
    <div className="shs">
      <div className="shs-head">
        <div>
          <h1>Site Institucional</h1>
          <p>Gerencie vagas, currículos, conteúdo, o bot e os leads do site.</p>
        </div>
        <a className="shs-btn ghost" href={SITE_URL} target="_blank" rel="noopener noreferrer">
          <FaExternalLinkAlt /> Abrir site
        </a>
      </div>

      <nav className="shs-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`shs-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon} <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <div className="shs-body">
        {tab === 'vagas'      && <VagasTab />}
        {tab === 'curriculos' && <CurriculosTab />}
        {tab === 'conteudo'   && <ConteudoTab />}
        {tab === 'bot'        && <BotTab />}
        {tab === 'leads'      && <LeadsTab />}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* VAGAS                                                                       */
/* ========================================================================== */
const VAGA_VAZIA = { titulo: '', local: '', tipo: '', tags: '', descricao: '', status: 'ativa', ordem: 0 };

function VagasTab() {
  const [vagas, setVagas]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [editing, setEdit]  = useState(null); // null = fechado; {} = form aberto

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const { data } = await api.get('/api/admin/site/vagas');
      setVagas(data);
    } catch { toast.error('Falha ao carregar vagas.'); }
    finally { setLoad(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrirNova = () => setEdit({ ...VAGA_VAZIA });
  const abrirEdicao = (v) => setEdit({ ...v, tags: Array.isArray(v.tags) ? v.tags.join(', ') : '' });

  const salvar = async () => {
    if (!editing.titulo.trim()) return toast.warn('Informe o título.');
    const payload = {
      ...editing,
      tags: editing.tags.split(',').map((t) => t.trim()).filter(Boolean),
      ordem: Number(editing.ordem) || 0,
    };
    try {
      if (editing.id) await api.put(`/api/admin/site/vagas/${editing.id}`, payload);
      else await api.post('/api/admin/site/vagas', payload);
      toast.success('Vaga salva!');
      setEdit(null);
      load();
    } catch { toast.error('Falha ao salvar vaga.'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta vaga?')) return;
    try { await api.delete(`/api/admin/site/vagas/${id}`); toast.success('Vaga excluída.'); load(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  return (
    <div>
      <div className="shs-toolbar">
        <span className="shs-count">{vagas.length} vaga(s)</span>
        <button className="shs-btn primary" onClick={abrirNova}><FaPlus /> Nova vaga</button>
      </div>

      {loading ? <Loading /> : vagas.length === 0 ? <Empty texto="Nenhuma vaga cadastrada." /> : (
        <div className="shs-grid">
          {vagas.map((v) => (
            <div key={v.id} className="shs-card">
              <div className="shs-card-head">
                <strong>{v.titulo}</strong>
                <span className={`shs-badge ${v.status === 'ativa' ? 'ok' : 'off'}`}>{v.status}</span>
              </div>
              <div className="shs-muted">{[v.local, v.tipo].filter(Boolean).join(' · ')}</div>
              {Array.isArray(v.tags) && v.tags.length > 0 && (
                <div className="shs-tags">{v.tags.map((t) => <span key={t}>{t}</span>)}</div>
              )}
              <div className="shs-card-actions">
                <button onClick={() => abrirEdicao(v)}><FaEdit /> Editar</button>
                <button className="danger" onClick={() => excluir(v.id)}><FaTrash /> Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal titulo={editing.id ? 'Editar vaga' : 'Nova vaga'} onClose={() => setEdit(null)} onSave={salvar}>
          <Field label="Título"><input value={editing.titulo} onChange={(e) => setEdit({ ...editing, titulo: e.target.value })} /></Field>
          <div className="shs-row">
            <Field label="Local"><input value={editing.local} onChange={(e) => setEdit({ ...editing, local: e.target.value })} /></Field>
            <Field label="Tipo"><input placeholder="CLT / PJ" value={editing.tipo} onChange={(e) => setEdit({ ...editing, tipo: e.target.value })} /></Field>
          </div>
          <Field label="Tags (separadas por vírgula)"><input value={editing.tags} onChange={(e) => setEdit({ ...editing, tags: e.target.value })} /></Field>
          <Field label="Descrição"><textarea rows={4} value={editing.descricao} onChange={(e) => setEdit({ ...editing, descricao: e.target.value })} /></Field>
          <div className="shs-row">
            <Field label="Status">
              <select value={editing.status} onChange={(e) => setEdit({ ...editing, status: e.target.value })}>
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
              </select>
            </Field>
            <Field label="Ordem"><input type="number" value={editing.ordem} onChange={(e) => setEdit({ ...editing, ordem: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ========================================================================== */
/* CURRÍCULOS                                                                  */
/* ========================================================================== */
const CV_STATUS = ['novo', 'em_analise', 'aprovado', 'reprovado'];

function CurriculosTab() {
  const [data, setData]    = useState({ items: [], page: 1, totalPages: 1 });
  const [page, setPage]    = useState(1);
  const [loading, setLoad] = useState(true);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const resp = await api.get(`/api/admin/site/curriculos?page=${page}`);
      setData(resp.data);
    } catch { toast.error('Falha ao carregar currículos.'); }
    finally { setLoad(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const mudarStatus = async (id, status) => {
    try { await api.put(`/api/admin/site/curriculos/${id}/status`, { status }); load(); }
    catch { toast.error('Falha ao atualizar status.'); }
  };

  const abrirCV = async (driveId) => {
    if (!driveId) return toast.info('Este candidato não anexou arquivo.');
    try {
      const resp = await api.get(`/api/anexos/drive/${driveId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Não foi possível abrir o CV.'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este currículo? O arquivo no Drive também será removido.')) return;
    try { await api.delete(`/api/admin/site/curriculos/${id}`); toast.success('Currículo excluído.'); load(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  if (loading) return <Loading />;
  if (data.items.length === 0) return <Empty texto="Nenhum currículo recebido ainda." />;

  return (
    <div>
      <table className="shs-table">
        <thead>
          <tr><th>Candidato</th><th>Contato</th><th>Vaga</th><th>Status</th><th>CV</th><th></th></tr>
        </thead>
        <tbody>
          {data.items.map((c) => (
            <tr key={c.id}>
              <td>
                <strong>{c.nome}</strong>
                {c.area_interesse && <div className="shs-muted">{c.area_interesse}</div>}
              </td>
              <td>
                <div>{c.email}</div>
                {c.telefone && <div className="shs-muted">{c.telefone}</div>}
              </td>
              <td>{c.vaga_titulo || <span className="shs-muted">—</span>}</td>
              <td>
                <select value={c.status} onChange={(e) => mudarStatus(c.id, e.target.value)}>
                  {CV_STATUS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </td>
              <td>
                {c.arquivo_drive_id
                  ? <button className="shs-link" onClick={() => abrirCV(c.arquivo_drive_id)}><FaExternalLinkAlt /> Abrir</button>
                  : <span className="shs-muted">sem arquivo</span>}
              </td>
              <td><button className="shs-icon danger" onClick={() => excluir(c.id)}><FaTrash /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager data={data} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(data.totalPages, p + 1))} />
    </div>
  );
}

/* ========================================================================== */
/* CONTEÚDO                                                                    */
/* ========================================================================== */
function ConteudoTab() {
  const [blocos, setBlocos] = useState([]);
  const [loading, setLoad]  = useState(true);
  const [drafts, setDrafts] = useState({}); // chave -> texto JSON em edição

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const { data } = await api.get('/api/admin/site/conteudo');
      setBlocos(data);
      const d = {};
      data.forEach((b) => { d[b.chave] = JSON.stringify(b.valor ?? {}, null, 2); });
      setDrafts(d);
    } catch { toast.error('Falha ao carregar conteúdo.'); }
    finally { setLoad(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvar = async (chave, descricao) => {
    let valor;
    try { valor = JSON.parse(drafts[chave] || '{}'); }
    catch { return toast.error('JSON inválido neste bloco.'); }
    try { await api.put(`/api/admin/site/conteudo/${chave}`, { valor, descricao }); toast.success(`Bloco "${chave}" salvo.`); }
    catch { toast.error('Falha ao salvar bloco.'); }
  };

  const novoBloco = async () => {
    const chave = window.prompt('Nome do bloco (ex.: hero, sobre, numeros):');
    if (!chave) return;
    const slug = chave.trim().toLowerCase().replace(/\s+/g, '_');
    try {
      await api.put(`/api/admin/site/conteudo/${slug}`, { valor: {}, descricao: '' });
      toast.success('Bloco criado.');
      load();
    } catch { toast.error('Falha ao criar bloco.'); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="shs-toolbar">
        <span className="shs-count">{blocos.length} bloco(s)</span>
        <button className="shs-btn primary" onClick={novoBloco}><FaPlus /> Novo bloco</button>
      </div>
      {blocos.length === 0 ? <Empty texto="Nenhum bloco de conteúdo ainda." /> : blocos.map((b) => (
        <div key={b.chave} className="shs-block">
          <div className="shs-block-head">
            <div>
              <strong>{b.chave}</strong>
              {b.descricao && <span className="shs-muted"> — {b.descricao}</span>}
            </div>
            <button className="shs-btn primary sm" onClick={() => salvar(b.chave, b.descricao)}><FaSave /> Salvar</button>
          </div>
          <textarea
            className="shs-json"
            rows={8}
            value={drafts[b.chave] || ''}
            onChange={(e) => setDrafts({ ...drafts, [b.chave]: e.target.value })}
            spellCheck={false}
          />
        </div>
      ))}
      <p className="shs-hint">Cada bloco é um JSON livre. Ex.: <code>{`{ "titulo": "...", "subtitulo": "..." }`}</code></p>
    </div>
  );
}

/* ========================================================================== */
/* BOT                                                                         */
/* ========================================================================== */
function BotTab() {
  const [cfg, setCfg]      = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/api/admin/site/bot-config'); setCfg(data || {}); }
      catch { toast.error('Falha ao carregar config do bot.'); }
      finally { setLoad(false); }
    })();
  }, []);

  const salvar = async () => {
    try { await api.put('/api/admin/site/bot-config', cfg); toast.success('Bot atualizado!'); }
    catch { toast.error('Falha ao salvar.'); }
  };

  if (loading || !cfg) return <Loading />;

  return (
    <div className="shs-form-card">
      <label className="shs-switch">
        <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked ? 1 : 0 })} />
        <span>Bot ativo no site</span>
      </label>
      <Field label="Título do widget"><input value={cfg.titulo || ''} onChange={(e) => setCfg({ ...cfg, titulo: e.target.value })} /></Field>
      <Field label="WhatsApp (só dígitos, com DDI/DDD)"><input placeholder="5512997477262" value={cfg.whatsapp || ''} onChange={(e) => setCfg({ ...cfg, whatsapp: e.target.value })} /></Field>
      <Field label="Mensagem padrão"><textarea rows={3} value={cfg.mensagem_padrao || ''} onChange={(e) => setCfg({ ...cfg, mensagem_padrao: e.target.value })} /></Field>
      <div className="shs-row">
        <Field label="Horário início"><input type="time" value={cfg.horario_inicio || ''} onChange={(e) => setCfg({ ...cfg, horario_inicio: e.target.value })} /></Field>
        <Field label="Horário fim"><input type="time" value={cfg.horario_fim || ''} onChange={(e) => setCfg({ ...cfg, horario_fim: e.target.value })} /></Field>
      </div>
      <button className="shs-btn primary" onClick={salvar}><FaSave /> Salvar configuração</button>
    </div>
  );
}

/* ========================================================================== */
/* LEADS                                                                       */
/* ========================================================================== */
const LEAD_STATUS = ['novo', 'em_contato', 'ganho', 'perdido'];

function LeadsTab() {
  const [data, setData]      = useState({ items: [], page: 1, totalPages: 1 });
  const [page, setPage]      = useState(1);
  const [origem, setOrigem]  = useState('');
  const [loading, setLoad]   = useState(true);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const q = new URLSearchParams({ page });
      if (origem) q.set('origem', origem);
      const resp = await api.get(`/api/admin/site/leads?${q.toString()}`);
      setData(resp.data);
    } catch { toast.error('Falha ao carregar leads.'); }
    finally { setLoad(false); }
  }, [page, origem]);

  useEffect(() => { load(); }, [load]);

  const mudarStatus = async (id, status) => {
    try { await api.put(`/api/admin/site/leads/${id}/status`, { status }); load(); }
    catch { toast.error('Falha ao atualizar.'); }
  };
  const excluir = async (id) => {
    if (!window.confirm('Excluir este lead?')) return;
    try { await api.delete(`/api/admin/site/leads/${id}`); load(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  return (
    <div>
      <div className="shs-toolbar">
        <select value={origem} onChange={(e) => { setPage(1); setOrigem(e.target.value); }}>
          <option value="">Todas as origens</option>
          <option value="contato">Contato</option>
          <option value="demo">Demonstração</option>
          <option value="bot">Bot</option>
        </select>
      </div>

      {loading ? <Loading /> : data.items.length === 0 ? <Empty texto="Nenhum lead ainda." /> : (
        <>
          <table className="shs-table">
            <thead>
              <tr><th>Nome</th><th>Contato</th><th>Origem</th><th>Mensagem</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <strong>{l.nome}</strong>
                    {l.empresa && <div className="shs-muted">{l.empresa}</div>}
                  </td>
                  <td>
                    {l.email && <div>{l.email}</div>}
                    {l.telefone && <div className="shs-muted">{l.telefone}</div>}
                  </td>
                  <td><span className="shs-badge">{l.origem}</span></td>
                  <td className="shs-msg">{l.mensagem || <span className="shs-muted">—</span>}</td>
                  <td>
                    <select value={l.status} onChange={(e) => mudarStatus(l.id, e.target.value)}>
                      {LEAD_STATUS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </td>
                  <td><button className="shs-icon danger" onClick={() => excluir(l.id)}><FaTrash /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager data={data} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(data.totalPages, p + 1))} />
        </>
      )}
    </div>
  );
}

/* ========================================================================== */
/* UI helpers                                                                  */
/* ========================================================================== */
const Loading = () => <div className="shs-state">Carregando…</div>;
const Empty = ({ texto }) => <div className="shs-state">{texto}</div>;
const Field = ({ label, children }) => (
  <label className="shs-field"><span>{label}</span>{children}</label>
);

const Pager = ({ data, onPrev, onNext }) => (
  <div className="shs-pager">
    <button disabled={data.page <= 1} onClick={onPrev}>← Anterior</button>
    <span>Página {data.page} de {data.totalPages}</span>
    <button disabled={data.page >= data.totalPages} onClick={onNext}>Próxima →</button>
  </div>
);

const Modal = ({ titulo, children, onClose, onSave }) => (
  <div className="shs-modal-overlay" onClick={onClose}>
    <div className="shs-modal" onClick={(e) => e.stopPropagation()}>
      <div className="shs-modal-head">
        <h3>{titulo}</h3>
        <button className="shs-icon" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="shs-modal-body">{children}</div>
      <div className="shs-modal-foot">
        <button className="shs-btn ghost" onClick={onClose}>Cancelar</button>
        <button className="shs-btn primary" onClick={onSave}><FaSave /> Salvar</button>
      </div>
    </div>
  </div>
);